import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../services/apiClient';
import { FaBell, FaExclamationTriangle, FaTimes } from 'react-icons/fa';

const LOW_STOCK_THRESHOLD = 10;
const REFRESH_INTERVAL_MS = 60000;

// Shows a bell with low/out-of-stock counts. Pass `franchiseData` to scope the
// alerts to that franchise's own covered pincodes; omit it for a global (admin) view.
// Pass `align="left"` when mounting inside a narrow container (e.g. the desktop
// sidebar) so the dropdown opens rightward instead of overflowing off-screen.
const LowStockNotifications = ({ franchiseData, align = 'right' }) => {
    const [products, setProducts] = useState([]);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await apiClient.get('/products');
                setProducts(res.data);
            } catch (error) {
                console.error('Failed to fetch products for stock alerts:', error);
            }
        };
        fetchProducts();
        const interval = setInterval(fetchProducts, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const alerts = [];
    products.forEach(product => {
        if (franchiseData) {
            // Franchise sees local inventory
            if (product.pincodePricing && product.pincodePricing.length > 0) {
                product.pincodePricing.forEach(row => {
                    if (!franchiseData.pincodesCovered?.includes(row.pincode)) return;
                    const stock = row.inventory || 0;
                    if (stock <= LOW_STOCK_THRESHOLD) {
                        alerts.push({
                            key: `${product._id}-${row._id}`,
                            name: product.name,
                            detail: `${row.size || ''} · Pincode ${row.pincode}`,
                            stock
                        });
                    }
                });
            }
        } else {
            // Admin sees global inventory only
            (product.variants || []).forEach((v, i) => {
                const stock = v.countInStock || 0;
                if (stock <= LOW_STOCK_THRESHOLD) {
                    alerts.push({
                        key: `${product._id}-variant-${i}`,
                        name: product.name,
                        detail: v.size || 'General Inventory',
                        stock
                    });
                }
            });
        }
    });

    alerts.sort((a, b) => a.stock - b.stock);
    const outOfStockCount = alerts.filter(a => a.stock === 0).length;
    const lowStockCount = alerts.length - outOfStockCount;

    // Load read alerts from local storage
    const [readAlertKeys, setReadAlertKeys] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('readStockAlerts')) || [];
        } catch {
            return [];
        }
    });

    const unreadAlerts = alerts.filter(a => !readAlertKeys.includes(a.key));

    const handleMarkAllAsRead = () => {
        const allKeys = alerts.map(a => a.key);
        setReadAlertKeys(allKeys);
        localStorage.setItem('readStockAlerts', JSON.stringify(allKeys));
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={() => setOpen(prev => !prev)}
                className="relative p-2.5 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
                title="Stock alerts"
            >
                <FaBell className={unreadAlerts.length > 0 ? 'text-yellow-600' : 'text-gray-400'} />
                {unreadAlerts.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {unreadAlerts.length > 99 ? '99+' : unreadAlerts.length}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setOpen(false)} />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-300 z-50 ring-1 ring-black/5">
                    <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-xl flex items-center justify-between gap-2">
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                <FaExclamationTriangle className="text-yellow-500" />
                                Stock Alerts
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                                {outOfStockCount} out of stock · {lowStockCount} running low
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadAlerts.length > 0 && (
                                <button
                                    onClick={handleMarkAllAsRead}
                                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors whitespace-nowrap"
                                >
                                    Mark all as read
                                </button>
                            )}
                            <button
                                onClick={() => setOpen(false)}
                                className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-1.5 transition-colors shrink-0"
                                title="Close"
                            >
                                <FaTimes size={12} />
                            </button>
                        </div>
                    </div>
                    {alerts.length === 0 ? (
                        <div className="p-6 text-center text-sm text-gray-400">All stock levels look healthy.</div>
                    ) : (
                        <div className="divide-y divide-gray-50">
                            {alerts.slice(0, 30).map(a => (
                                <div key={a.key} className="p-3 flex items-center justify-between gap-3 hover:bg-gray-50">
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold text-gray-800 truncate">{a.name}</p>
                                        <p className="text-[10px] text-gray-400 truncate">{a.detail}</p>
                                    </div>
                                    <span className={`text-[10px] font-black px-2 py-1 rounded-full whitespace-nowrap ${a.stock === 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                                        {a.stock === 0 ? 'OUT' : `${a.stock} left`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                </>
            )}
        </div>
    );
};

export default LowStockNotifications;
